import { useMatches } from "@remix-run/react";
import { AppConfiguration } from "../db/appConfiguration.db.server";
import { UserSession } from "../session.server";
import { MetaTagsDto } from "~/application/dtos/seo/MetaTagsDto";
import { ImpersonatingSessionDto } from "~/application/dtos/session/ImpersonatingSessionDto";
import { UserWithoutPassword } from "../db/users.db.server";
import { TenantSimple } from "../db/tenants.db.server";

export type AppRootData = {
  metatags: MetaTagsDto;
  user: UserWithoutPassword | null;
  currentTenant: TenantSimple | null;
  theme: string;
  locale: string;
  serverUrl: string;
  domainName: string;
  userSession: UserSession;
  authenticated: boolean;
  debug: boolean;
  isStripeTest: boolean;
  chatWebsiteId?: string;
  appConfiguration: AppConfiguration;
  csrf?: string;
  featureFlags: string[];
  impersonatingSession: ImpersonatingSessionDto | null;
};

// Create a safe default AppRootData with reasonable defaults
// to prevent undefined errors in components that use it
const defaultRootData: Partial<AppRootData> = {
  metatags: [],
  user: null,
  currentTenant: null,
  authenticated: false,
  featureFlags: [],
  impersonatingSession: null,
  theme: "light",
  locale: "en",
  serverUrl: "",
  domainName: "",
  // Critical: provide default userSession with cookies array to prevent errors
  userSession: {
    userId: "",
    lightOrDarkMode: "light",
    lng: "en",
    // Empty cookies array - CookieHelper.requiresCookieConsent will handle this
    cookies: [],
    userAnalyticsId: "",
  } as UserSession,
  // More complete appConfiguration based on the structure in appConfiguration.db.server.ts
  appConfiguration: {
    app: {
      name: "",
      url: "",
      domain: "",
      theme: "light",
      supportedThemes: "light-and-dark",
      features: {
        tenantHome: "/app/:tenant/",
        tenantApiKeys: false,
        tenantEntityCustomization: false,
        tenantTypes: true,
        tenantBlogs: false,
        tenantWorkflows: false,
        tenantEmailMarketing: false,
        linkedAccounts: false,
        tenantFeedback: false,
        surveys: true,
      },
    },
    auth: {
      requireEmailVerification: false,
      requireOrganization: false,
      requireName: true,
      slug: { require: false, type: "username" },
      recaptcha: {
        enabled: false,
        siteKey: "",
      },
      authMethods: {
        emailPassword: {
          enabled: true,
        },
        github: {
          enabled: false,
          authorizationURL: "",
        },
        google: {
          enabled: false,
        },
      },
    },
    analytics: {
      enabled: false,
      googleAnalyticsTrackingId: undefined,
      simpleAnalytics: false,
      plausibleAnalytics: false,
    },
    subscription: {
      required: true,
      allowSubscribeBeforeSignUp: false,
      allowSignUpBeforeSubscribe: true,
      multiple: false,
    },
    cookies: {
      enabled: false,
    },
    notifications: {
      enabled: false,
      novuAppId: undefined,
    },
    onboarding: {
      enabled: false,
    },
    featureFlags: {
      enabled: false,
    },
    metrics: {
      enabled: false,
      logToConsole: false,
      saveToDatabase: false,
      ignoreUrls: [],
    },
    branding: {
      logo: undefined,
      logoDarkMode: undefined,
      icon: undefined,
      iconDarkMode: undefined,
      favicon: undefined,
    },
    launches: undefined,
    portals: {
      enabled: false,
      forTenants: false,
      pricing: false,
      analytics: false,
      metadata: undefined,
      pages: []
    },
    scripts: { head: null, body: null },
  }
};

export function useRootData(): AppRootData {
  // Find root route data from useMatches
  const matches = useMatches();
  const rootRouteMatch = matches.find(match => 
    match.pathname === "/" || match.pathname === "" || match.id === "root"
  );
  

  
  // If no root route data is found, log a warning in development
  if (!rootRouteMatch?.data && process.env.NODE_ENV === 'development') {
    console.warn('useRootData: No root route data found in useMatches()', 
      { availableRoutes: matches.map(m => ({ id: m.id, pathname: m.pathname })) }
    );
  }

  // Merge defaults with server data - server data should override defaults
  const result = { 
    ...defaultRootData, 
    ...(rootRouteMatch?.data || {}),
    // Ensure appConfiguration is properly merged (server overrides defaults)
    appConfiguration: {
      ...defaultRootData.appConfiguration,
      ...(rootRouteMatch?.data as any)?.appConfiguration
    }
  } as AppRootData;
  
  return result;
}
